import React, { useEffect, useState } from 'react';
import { useTalentos } from '../../../../application/talentos/hooks/useTalentos';
import ToolbarComponent from '../../../ui/section';
import Loader from '../../../ui/loader';
import './detail.css';

const JobDetailPage = (props) => {
    const { getJobById, isLoading, error } = useTalentos();
    const [job, setJob] = useState(null);

    useEffect(() => {
        const id = props.match?.params?.id;
        if (id) {
            loadJob(id);
        }
    }, [props.match?.params?.id]);

    const loadJob = async (id) => {
        try {
            const data = await getJobById(id);
            setJob(data);
        } catch (err) {
            console.error('Error al cargar trabajo:', err);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatCurrency = (amount, currency = 'CLP') => {
        if (!amount) return '-';
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: currency
        }).format(amount);
    };

    const getStatusLabel = (status) => {
        const labels = {
            active: 'Activo',
            closed: 'Cerrado',
            expired: 'Expirado'
        };
        return labels[status] || status;
    };

    const getStatusColor = (status) => {
        const colors = {
            active: '#10B981',
            closed: '#EF4444',
            expired: '#F59E0B'
        };
        return colors[status] || '#6B7280';
    };

    if (isLoading) {
        return (
            <ToolbarComponent label="Detalle del Trabajo" showBackButton={true} onBackClick={() => props.history.goBack()}>
                <Loader text="Cargando trabajo..." />
            </ToolbarComponent>
        );
    }

    if (error || !job) {
        return (
            <ToolbarComponent label="Detalle del Trabajo" showBackButton={true} onBackClick={() => props.history.goBack()}>
                <div className="error-message">
                    {error || 'No se pudo cargar el trabajo'}
                </div>
            </ToolbarComponent>
        );
    }

    return (
        <ToolbarComponent label="Detalle del Trabajo" showBackButton={true} onBackClick={() => props.history.goBack()}>
            <div className="job-detail-container">
                {/* Header */}
                <div className="detail-header">
                    <div>
                        <h1 className="detail-title">{job.title || 'Sin título'}</h1>
                        {job.company && (
                            <p className="detail-company">
                                {typeof job.company === 'object' ? job.company.name : job.company}
                            </p>
                        )}
                    </div>
                    <div className="detail-status">
                        <span 
                            className="status-badge"
                            style={{ 
                                background: `${getStatusColor(job.status)}20`,
                                color: getStatusColor(job.status)
                            }}
                        >
                            {getStatusLabel(job.status)}
                        </span>
                    </div>
                </div>

                {/* Información Principal */}
                <div className="detail-section">
                    <h2 className="section-title">Información General</h2>
                    <div className="info-grid">
                        <div className="info-item">
                            <span className="info-label">Categoría</span>
                            <span className="info-value">{job.category || '-'}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">País</span>
                            <span className="info-value">{job.country || '-'}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Ciudad</span>
                            <span className="info-value">{job.city || '-'}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Fecha de publicación</span>
                            <span className="info-value">{formatDate(job.posted_at)}</span>
                        </div>
                        {job.featured && (
                            <div className="info-item">
                                <span className="info-label">Destacado</span>
                                <span className="info-value">Sí</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Compensación */}
                {job.compensation && (
                    <div className="detail-section">
                        <h2 className="section-title">Compensación</h2>
                        <div className="compensation-info">
                            {job.compensation.min && job.compensation.max ? (
                                <div className="compensation-range">
                                    {formatCurrency(job.compensation.min, job.compensation.currency)} - {formatCurrency(job.compensation.max, job.compensation.currency)}
                                </div>
                            ) : job.compensation.min ? (
                                <div className="compensation-range">
                                    Desde {formatCurrency(job.compensation.min, job.compensation.currency)}
                                </div>
                            ) : (
                                <div className="compensation-range">A convenir</div>
                            )}
                            {job.compensation.currency && (
                                <span className="compensation-currency">({job.compensation.currency})</span>
                            )}
                        </div>
                    </div>
                )}

                {/* Descripción */}
                {job.description && (
                    <div className="detail-section">
                        <h2 className="section-title">Descripción</h2>
                        <div className="description-content">
                            {job.description.split('\n').map((paragraph, index) => (
                                <p key={index}>{paragraph}</p>
                            ))}
                        </div>
                    </div>
                )}

                {/* Requisitos */}
                {job.requirements && job.requirements.length > 0 && (
                    <div className="detail-section">
                        <h2 className="section-title">Requisitos</h2>
                        <ul className="requirements-list">
                            {job.requirements.map((req, index) => (
                                <li key={index}>{req}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Información Adicional */}
                <div className="detail-section">
                    <h2 className="section-title">Información Adicional</h2>
                    <div className="info-grid">
                        {job.region && (
                            <div className="info-item">
                                <span className="info-label">Región</span>
                                <span className="info-value">{job.region}</span>
                            </div>
                        )}
                        {job.type && (
                            <div className="info-item">
                                <span className="info-label">Tipo</span>
                                <span className="info-value">{job.type}</span>
                            </div>
                        )}
                        {job.remote && (
                            <div className="info-item">
                                <span className="info-label">Remoto</span>
                                <span className="info-value">Sí</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </ToolbarComponent>
    );
};

export default JobDetailPage;









